import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  getAllProductsWithoutPagination,
  getSale,
  handBackTheProduct,
  verifySaleTheProduct,
} from "../../../redux/features/assetManagementSlice";
import { savePaginationData } from "../../../redux/features/globalDatagridSlice";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  CancelButton,
  SubmitButton,
} from "../../../components/mui/Buttons/Buttons";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { Icon } from "@iconify/react/dist/iconify.js";

const HandTheProductBack = ({ notDelivered, row, dispatchParams }) => {
  const [open, setOpen] = useState(false);
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, productData } = useSelector(
    (store) => store.assetmanagement
  );

  const initialValues = {
    damagedReason: "",
    verified: false,
    isDamaged: false,
    isPhysicallyDamaged: false,
    damagedAmount: "",
  };

  const schema = Yup.object({
    verified: Yup.boolean().oneOf([true], "Verification is required"),
    damagedAmount: Yup.number().when("isPhysicallyDamaged", {
      is: true,
      then: Yup.number()
        .required("Fine amount is required")
        .min(0, "Fine amount cannot be negative"),
      otherwise: Yup.number().notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const data = { ...values };
      if (row) {
        const updateData = {
          ...data,
          id: row.salesID,
          damagedAmount: values.isPhysicallyDamaged
            ? values.damagedAmount
            : null,
        };
        dispatch(handBackTheProduct(updateData)).then((res) => {
          if (res.payload.status === "error") {
            enqueueSnackbar(res.payload.message, {
              variant: "error",
            });
          } else if (res.payload.status === "success") {
            handleCancel();
            dispatch(getSale(dispatchParams));
            dispatch(
              savePaginationData({ ...paginationData, openAnchor: false })
            );
            enqueueSnackbar(res.payload.message, {
              variant: "success",
            });
          }
        });
      }
    },
  });

  const handleProductChange = (event, option) => {
    const fieldName = event.target?.id.split("-")[0];
    formik.setFieldValue(fieldName, option?.product_id);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <GlobalDialog
      maxWidth={"sm"}
      fullWidth
      title={"Hand The Product Back"}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      closeCondition={handleCancel}
      popupButton={
        <>
          <Button
            size="small"
            disabled={notDelivered}
            variant="outlined"
            onClick={() => setOpen(true)}
          >
            Return the product
          </Button>
        </>
      }
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box component={Paper} elevation={2} p={2} borderRadius={2}>
                <Typography variant="h6" gutterBottom>
                  Verification
                </Typography>

                <Stack direction={"row"} alignItems="center" spacing={1}>
                  <Icon
                    icon="arcticons:okta-verify"
                    width="25"
                    height="25"
                    style={{ color: "#7e4d8b" }}
                  />
                  <Box>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="verified"
                            checked={formik.values.verified}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            color="primary"
                          />
                        }
                        label="Verified"
                        sx={{ alignItems: "center" }}
                      />
                    </FormGroup>
                  </Box>
                </Stack>
                {formik.touched.verified && formik.errors.verified && (
                  <FormHelperText error sx={{ ml: 4 }}>
                    {formik.errors.verified}
                  </FormHelperText>
                )}

                <Stack direction={"row"} alignItems="center" spacing={1}>
                  <Icon
                    icon="game-icons:cracked-glass"
                    width="25"
                    height="25"
                    style={{ color: "#7e4d8b" }}
                  />
                  <Box>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isDamaged"
                            checked={formik.values.isDamaged}
                            onChange={formik.handleChange}
                            onBlur={formik.isDamaged}
                          />
                        }
                        label="Product Damaged"
                      />
                    </FormGroup>
                  </Box>
                </Stack>

                {formik.values.isDamaged && (
                  <Box>
                    <Stack direction={"row"} alignItems="center" spacing={1}>
                      <Icon
                        icon="game-icons:hammer-break"
                        width="25"
                        height="25"
                        style={{ color: "white", backgroundColor: "red" }}
                      />
                      <Box>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isPhysicallyDamaged"
                                checked={formik.values.isPhysicallyDamaged}
                                onChange={formik.handleChange}
                                onBlur={formik.isPhysicallyDamaged}
                              />
                            }
                            label="Physically Damaged"
                          />
                        </FormGroup>
                      </Box>
                    </Stack>

                    {formik.values.isPhysicallyDamaged && (
                      <Box mb={1}>
                        <TextField
                          autoComplete="off"
                          type="number"
                          fullWidth
                          id="damagedAmount"
                          label="Fine Amount"
                          name="damagedAmount"
                          value={formik.values.damagedAmount}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.damagedAmount &&
                            Boolean(formik.errors.damagedAmount)
                          }
                          helperText={
                            formik.touched.damagedAmount &&
                            formik.errors.damagedAmount
                          }
                          onWheel={(e) => e.target.blur()}
                          // inputProps={{
                          //   inputMode: "numeric",
                          //   pattern: "[0-9]*",
                          //   onWheel: (e) => e.target.blur(),
                          // }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                <TextField
                  autoComplete="off"
                  fullWidth
                  id="damagedReason"
                  label="Return Remarks"
                  name="damagedReason"
                  value={formik.values.damagedReason}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.damagedReason &&
                    Boolean(formik.errors.damagedReason)
                  }
                  helperText={
                    formik.touched.damagedReason && formik.errors.damagedReason
                  }
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Stack direction={"row"} spacing={1}>
                  <CancelButton action={() => handleCancel()}>
                    Cancel
                  </CancelButton>
                  <SubmitButton disabled={loading} type="submit">
                    {loading ? "Returning..." : "Return The Product"}
                  </SubmitButton>
                </Stack>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default HandTheProductBack;
